import React, { useState, useContext, useRef, useEffect, useMemo } from "react";

import classNames from "classnames";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import breakpoints from "../../constants";
import useDevice from "../../customHooks/useDevice";
import Markdown from "../../styles/Markdown";

const StyledMapSlideshow = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || "transparent"};
  ${(props) => props.$dynamicStyles}
`;

const StyledMapContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ $height, $mobileHeight }) => $mobileHeight || $height || "70vh"};
  overflow: hidden;

  @media (min-width: ${breakpoints.TABLET}) {
    min-height: ${({ $height }) => $height || "80vh"};
  }
`;

const StyledImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const StyledCarouselContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  .react-multi-carousel-item {
    height: 100%;
  }

  .react-multi-carousel-track {
    height: 100%;
  }

  .react-multi-carousel-list {
    height: 100%;
  }
`;

const StyledTabsWrapper = styled.div`
  position: absolute;
  top: 5rem;
  left: 0;
  z-index: 20;
  width: 100%;

  @media (min-width: ${breakpoints.MOBILE}) {
    top: 6rem;
  }

  @media (min-width: ${breakpoints.TABLET}) {
    top: 7rem;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    top: 8rem;
  }
`;

const StyledTabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  /* Ensure smooth scrolling on mobile */
  scroll-snap-type: x proximity;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (min-width: ${breakpoints.TABLET}) {
    gap: 1rem;
    padding: 2rem 1rem;
    justify-content: space-between;
    scroll-snap-type: none;
  }
`;

const StyledTab = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 2rem;
  border: none;
  background-color: ${({ isActive }) =>
    isActive ? "#ffffff" : "rgba(255, 255, 255, 0.3)"};
  color: ${({ isActive }) => (isActive ? "#333333" : "#666666")};
  font-size: 1rem;
  font-weight: ${({ isActive }) => (isActive ? "600" : "400")};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  min-height: 48px; /* Touch target size */
  white-space: nowrap; /* Prevent text wrapping */
  scroll-snap-align: start; /* Snap to start of tab when scrolling */
  flex: 1; /* Grow to fill available space */
  flex-basis: 0; /* Equal distribution */

  @media (min-width: ${breakpoints.TABLET}) {
    padding: 1rem 2rem;
    font-size: 1.125rem;
    min-height: 52px;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    padding: 1.125rem 2.25rem;
    font-size: 1.25rem;
    min-height: 56px;
  }

  &:hover {
    background-color: ${({ isActive }) =>
      isActive ? "#ffffff" : "rgba(255, 255, 255, 0.5)"};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const StyledTitle = styled.div`
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 21;
  text-align: center;
  color: #333333;
  font-size: 1.5rem;
  font-weight: bold;
  width: 100%;
  max-width: 90%;
  padding: 0 1rem;

  @media (min-width: ${breakpoints.MOBILE}) {
    font-size: 2rem;
    top: 1.5rem;
  }

  @media (min-width: ${breakpoints.TABLET}) {
    font-size: 2.5rem;
    top: 2rem;
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    font-size: 3.5rem;
    top: 2.5rem;
  }
`;

const StyledDescriptionDesktop = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  width: 100%;
  color: #333333;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 0;
  z-index: 15;
  display: none; /* Hidden on mobile */

  @media (min-width: ${breakpoints.TABLET}) {
    display: block;
    font-size: 1rem;
    
    div > div {
      max-width: 500px;
    }
  }

  @media (min-width: ${breakpoints.DESKTOP}) {
    font-size: 1.1rem;
    
    div > div {
      max-width: 600px;
    }
  }
`;

const StyledDescriptionMobile = styled.div`
  position: relative;
  width: 100%;
  color: #333333;
  font-size: 0.875rem;
  line-height: 1.6;
  padding: 2rem 1rem;
  display: block; /* Visible on mobile */

  @media (min-width: ${breakpoints.TABLET}) {
    display: none; /* Hidden on desktop */
  }
`;
const StyledClickableAreaWrapper = styled.div`
  position: absolute;
  left: ${({ x }) => x || "0%"};
  top: ${({ y }) => y || "0%"};
  z-index: 16;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  /* Larger hitbox area for easier clicking/tapping */
  padding: 8px;
  margin: -8px;
  transform: translate(-50%, -50%); /* Center the marker on the position */
  
  @media (min-width: ${breakpoints.TABLET}) {
    padding: 4px;
    margin: -4px;
  }
`;

const StyledClickableArea = styled.a`
  position: relative;
  cursor: pointer;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  touch-action: manipulation; /* Prevents double-tap zoom */

  @media (min-width: ${breakpoints.TABLET}) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    min-width: 120px;
  }

  /* Custom content styling */
  .custom-location-content {
    width: 100%;
    
    p {
      margin: 0;
    }
  }
`;

// Helper function to normalize position values
// Supports both percentages (recommended) and pixels (for backward compatibility)
const normalizePosition = (value) => {
  if (!value) return "0%";
  
  // If already a percentage, return as is
  if (typeof value === "string" && value.includes("%")) {
    return value;
  }
  
  // If it's a pixel value, convert to percentage (fallback for backward compatibility)
  // Note: This assumes a reference image size. For best results, use percentages directly.
  if (typeof value === "string" && value.includes("px")) {
    const pxValue = parseFloat(value);
    // Default reference: 1920x1080 (common hero image size)
    const refSize = 1920; // Using width as reference for both x and y
    const percentage = (pxValue / refSize) * 100;
    return `${percentage.toFixed(2)}%`;
  }
  
  // Return as is if it's already a valid CSS value
  return value;
};

function MapSlideshow() {
  const {
    title = "Map Slideshow Title",
    description = "Description text for the map slideshow component.",
    slides = [
      {
        tab: "Tab 1",
        image: {
          url: "https://via.placeholder.com/1200x800"
        },
        locations: [
          {
            name: "Location 1",
            url: "#",
            x: "50%",
            y: "50%",
            mobileX: "50%",
            mobileY: "50%"
          }
        ]
      },
      {
        tab: "Tab 2",
        image: {
          url: "https://via.placeholder.com/1200x800"
        },
        locations: []
      }
    ],
    minHeight = "100vh",
    mobileMinHeight = "80vh",
    CSS,
    wrapperClassName,
    fullWidth = false,
    backgroundColor,
  } = useContext(ComponentContext);

  const { isMobile } = useDevice();
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  // Sync carousel with tab clicks
  useEffect(() => {
    if (carouselRef.current && carouselRef.current.goToSlide) {
      carouselRef.current.goToSlide(currentIndex);
    }
  }, [currentIndex]);

  const rowClassName = classNames({ row: !fullWidth }, wrapperClassName);

  // Normalize slides array - handle both v5 format and direct array
  const slidesArray =
    Array.isArray(slides) && slides.length > 0
      ? slides
      : slides?.data || [];

  if (slidesArray.length === 0) {
    return null;
  }

  const currentSlide = slidesArray[currentIndex] || slidesArray[0];
  const currentLocations = currentSlide?.locations || [];

  // Process locations - normalize positions (percentages recommended, pixels supported for backward compatibility)
  // Memoize to avoid recalculating on every render
  const processedLocations = useMemo(() => {
    return currentLocations.map((location) => ({
      ...location,
      // Normalize desktop positions (percentages work best!)
      x: normalizePosition(location?.x),
      y: normalizePosition(location?.y),
      // Normalize mobile positions if provided
      mobileX: location?.mobileX ? normalizePosition(location.mobileX) : undefined,
      mobileY: location?.mobileY ? normalizePosition(location.mobileY) : undefined,
    }));
  }, [currentLocations]);

  return (
    <StyledMapSlideshow
      className={rowClassName}
      $dynamicStyles={CSS}
      $backgroundColor={backgroundColor}
    >
      <StyledMapContainer
        $height={minHeight}
        $mobileHeight={mobileMinHeight}
      >
        {title && (
          <StyledTitle>
            <Markdown>{title}</Markdown>
          </StyledTitle>
        )}

        {/* Tabs */}
        <StyledTabsWrapper>
          <div className="container-xxl">
            <StyledTabsContainer role="tablist">
              {slidesArray.map((slide, index) => (
                <StyledTab
                  key={slide?.tab || slide?.id || index}
                  isActive={currentIndex === index}
                  onClick={() => {
                    setCurrentIndex(index);
                  }}
                  type="button"
                  aria-label={`Switch to ${slide?.tab || slide?.label || `slide ${index + 1}`}`}
                  aria-selected={currentIndex === index}
                  role="tab"
                >
                  {slide?.tab || slide?.label || `Slide ${index + 1}`}
                </StyledTab>
              ))}
            </StyledTabsContainer>
          </div>
        </StyledTabsWrapper>

        {/* Description - Desktop: left side of map */}
        {description && (
          <StyledDescriptionDesktop>
            <div className="container-xxl">
              <div className="me-auto">
                <Markdown>{description}</Markdown>
              </div>
            </div>
          </StyledDescriptionDesktop>
        )}

        {/* Background Images */}
        <StyledCarouselContainer>
          <Carousel
            ref={carouselRef}
            responsive={{
              all: {
                breakpoint: { max: 4000, min: 0 },
                items: 1,
              },
            }}
            infinite={false}
            draggable={true}
            swipeable={true}
            arrows={false}
            showDots={false}
            containerClass="map-slideshow-carousel"
            afterChange={(previousSlide, { currentSlide }) => {
              setCurrentIndex(currentSlide);
            }}
            transitionDuration={600}
            customTransition="transform 600ms ease-in-out"
          >
            {slidesArray.map((slide, index) => {
              const slideImage = slide?.image;
              const slideImageUrl =
                slideImage?.url ||
                (Array.isArray(slideImage) && slideImage[0]?.url) ||
                "";

              if (!slideImageUrl) return null;

              return (
                <StyledImage
                  key={slide?.tab || slide?.id || index}
                  style={{
                    backgroundImage: `url(${slideImageUrl})`,
                  }}
                />
              );
            })}
          </Carousel>
        </StyledCarouselContainer>

        {processedLocations.map((location, locIndex) => {
          const areaX = isMobile && location?.mobileX ? location.mobileX : location?.x;
          const areaY = isMobile && location?.mobileY ? location.mobileY : location?.y;
          
          return (
            <StyledClickableAreaWrapper
              key={`wrapper-${currentIndex}-${location?.name || locIndex}`}
              x={areaX || "0%"}
              y={areaY || "0%"}
            >
                <StyledClickableArea
                  href={location?.url || "#"}
                  onClick={(e) => {
                    if (!location?.url || location?.url === "#") {
                      e.preventDefault();
                    }
                  }}
                >
                  {location?.content || location?.markup ? (
                    <div className="custom-location-content">
                      <Markdown>{location?.content || location?.markup}</Markdown>
                    </div>
                  ) : (
                    <div className="custom-location-content">
                      <div>{location?.name || "Location"}</div>
                      {location?.count !== undefined && (
                        <div style={{ fontSize: "0.75rem", opacity: 0.9, marginTop: "0.25rem" }}>
                          {location.count} {location.count === 1 ? "restaurant" : "restaurants"}
                        </div>
                      )}
                    </div>
                  )}
                </StyledClickableArea>
              </StyledClickableAreaWrapper>
            );
          })}
      </StyledMapContainer>

      {/* Description - Mobile: below the map */}
      {description && (
        <StyledDescriptionMobile>
          <div className="container-xxl">
            <Markdown>{description}</Markdown>
          </div>
        </StyledDescriptionMobile>
      )}
    </StyledMapSlideshow>
  );
}

export default MapSlideshow;
